import { VideoData } from '@lumen5/luminary';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import {
    formatNarration,
    formatNarrationWindow,
    narrationWindowAt,
    type TimedWord,
    timeNarrationWords,
} from '../html-design/sceneNarration';
import {
    captureSceneFrames,
    CAPTURE_MODE_PROGRESS,
    SCREENSHOT_MAX_EDGE,
} from '../rendering/sceneScreenshot';
import {
    type EnhancedToolResult,
    type ImageContent,
    Model,
    ReasoningEffort,
    type TextContent,
    type ToolExecutionContext,
} from '../types';

import { ConversationTool } from './ConversationTool';
import { ReportFindingsTool } from './ReportFindingsTool';
import { reviewSkippedResult } from './review-skipping';
import {
    applicableChecks,
    type ReviewCheck,
    buildReviewInstruction,
    findingsIdentitySignature,
    findingsSignature,
    formatReviewGate,
    type ReviewFinding,
} from './review-checks';
import { resolveSceneById, sceneDisplayId } from './scene-addressing';
import { getSceneTranscriptTokens } from './transcript-partition';

import { enterCreationStage, isReviewSkipped, setSceneReviewing } from 'js/src/data/agent-activity';

/** Narration cues print to one decimal, so a second read off one can sit this far past the end. */
const CUE_ROUNDING_TOLERANCE_S = 0.05;

/**
 * With no severity tier, a finding the model won't stop reporting has nothing else to release it —
 * this cap is what stops the agent looping forever on a defect it cannot fix.
 */
const MAX_REVIEW_ROUNDS = 3;

/**
 * Tracks blocking rounds and each scene's last finding set so a repeat can be called out and the
 * round cap enforced. Keyed `${conversationId}:${sceneId}`, matching `applyHtmlAndLint`'s own
 * repeat-detection key (htmlAssetToolHelpers.ts) — module-level because the tool is re-instantiated
 * per call, but the count has to survive across calls within the same conversation. A finding that
 * can't be attributed to a requested scene is keyed `${conversationId}:__unattributed__` instead,
 * so it is capped and released the same way rather than blocking forever.
 *
 * Two different identities are tracked per key, answering two different questions:
 * `lastIdentityByScene` (coarse: check + scene_id) drives the round cap — the reviewer is
 * re-describing freshly rendered frames every round and isn't required to phrase the same defect
 * identically, so gating the cap on exact wording would let any reworded sentence reset an
 * unfixable defect's count forever. `lastSignatureByScene` (full: check + scene_id + detail) drives
 * the "unchanged from your previous attempt" wording, which is a stronger, deliberately
 * byte-exact claim that an edit provably changed nothing.
 */
const blockingRoundsByScene = new Map<string, number>();
const lastIdentityByScene = new Map<string, string>();
const lastSignatureByScene = new Map<string, string>();

/** Test-only: clears the module-level round-tracking state so specs don't leak into each other. */
export function resetReviewRoundsForTest(): void {
    blockingRoundsByScene.clear();
    lastIdentityByScene.clear();
    lastSignatureByScene.clear();
}

function secondsWithinScene(seconds: number, sceneDurationMs: number): boolean {
    const sceneSeconds = sceneDurationMs / 1000;
    return (
        Number.isFinite(seconds) &&
        sceneSeconds > 0 &&
        seconds >= 0 &&
        seconds <= sceneSeconds + CUE_ROUNDING_TOLERANCE_S
    );
}

export const reviewSceneImagesSchema = z.object({
    scene_ids: z.array(z.string()),
    seconds: z
        .number()
        .optional()
        .describe(
            'Review one instant of each scene instead of scanning its whole timeline — for ' +
                "checking a phase that settles between the scan's samples. Checks that need " +
                'the whole timeline are skipped.',
        ),
});
export type ReviewSceneImagesArgs = z.infer<typeof reviewSceneImagesSchema>;

type ResolvedScene = { sceneId: string; sceneIndex: number };

/**
 * Renders scenes and hands them to a fresh vision model that judges them against the closed
 * defect list in `review-checks.ts`, reporting through `ReportFindingsTool` rather than prose.
 * Every finding blocks (`isSuccess: false`) — there is no severity tier — so a defect the model
 * would otherwise soften into a suggestion instead arrives in the same failure shape as a lint
 * error, and gets fixed rather than shipped.
 */
export class ReviewSceneImagesTool extends ConversationTool<typeof reviewSceneImagesSchema> {
    public override canRunAsync = true;
    public override readonly childModel = Model.GPT_5_6_LUNA;
    // The review judges a closed list, applies the frame-count rules, and — with no severity
    // tier — makes the include/exclude call at the reporting threshold. That calibration is
    // the only filter between the agent and a wall of commentary, so it gets more than LOW.
    public override readonly childReasoningEffort = ReasoningEffort.MEDIUM;
    protected override readonly appendsVideoDiff = false;

    protected override readonly schema = reviewSceneImagesSchema;
    protected override readonly toolName = TOOL_NAMES.reviewSceneImages;
    protected override readonly toolDescription =
        'Render scenes and check them against a closed list of visual defects. Returns a failure ' +
        'listing every defect found, which you must fix and re-review, or a pass when the scenes ' +
        'are clean.';

    public override getToolDisplayName(): string {
        return 'Reviewing scene';
    }

    public get statusIcon(): string {
        return 'image_search';
    }

    public async execute(
        args: ReviewSceneImagesArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        if (isReviewSkipped(this.getState())) {
            return { result: reviewSkippedResult(), isSuccess: true };
        }

        this.dispatch(enterCreationStage({ stage: 'reviewing' }));

        if (args.scene_ids.length === 0) {
            return {
                result: 'Error: provide at least one scene id (scene_ids) to review.',
                isSuccess: false,
            };
        }

        const resolvedScenes: ResolvedScene[] = [];
        for (const sceneId of args.scene_ids) {
            const resolved = resolveSceneById(this.luminary, this.getState(), sceneId);
            if ('error' in resolved) {
                return { result: `Error: ${resolved.error}`, isSuccess: false };
            }
            resolvedScenes.push({ sceneId, sceneIndex: resolved.sceneIndex });
        }

        const reviewingSceneIds = resolvedScenes.map(
            ({ sceneIndex }) => this.luminary.scenes[sceneIndex].id,
        );
        this.dispatch(setSceneReviewing({ sceneIds: reviewingSceneIds, isReviewing: true }));
        // A reviewed scene is considered filled — clear its build-preview blur right away
        // rather than after the vision call returns.
        this.recordSceneFillForScenes(
            resolvedScenes.map(({ sceneIndex }) => sceneIndex),
            'review',
        );
        try {
            return await this.reviewScenes(args, context, resolvedScenes);
        } finally {
            this.dispatch(setSceneReviewing({ sceneIds: reviewingSceneIds, isReviewing: false }));
        }
    }

    private async reviewScenes(
        args: ReviewSceneImagesArgs,
        context: ToolExecutionContext,
        resolvedScenes: ResolvedScene[],
    ): Promise<EnhancedToolResult> {
        const hasScan = args.seconds === undefined;

        if (args.seconds !== undefined) {
            const missing = resolvedScenes
                .map(({ sceneId, sceneIndex }) => ({
                    sceneId,
                    scene: this.luminary.scenes[sceneIndex],
                }))
                .filter(({ scene }) => scene && !secondsWithinScene(args.seconds!, scene.duration));
            if (missing.length > 0) {
                const bounds = missing
                    .map(
                        ({ sceneId, scene }) =>
                            `${sceneDisplayId(this.luminary, sceneId)} runs 0–${(scene!.duration / 1000).toFixed(2)}s`,
                    )
                    .join('; ');
                return {
                    result: `Error: ${args.seconds}s is outside the scene(s) asked about — ${bounds}.`,
                    isSuccess: false,
                };
            }
        }

        // An html graphic is judged on staging and phase transitions; a template scene
        // animates in and settles, so those checks would report the norm rather than a defect.
        // The two groups therefore need different check lists, and a list is per conversation —
        // hence one pass each, merged below.
        const groups = [
            {
                isHtmlGraphic: true,
                scenes: resolvedScenes.filter((s) => this.isHtmlGraphic(s)),
            },
            {
                isHtmlGraphic: false,
                scenes: resolvedScenes.filter((s) => !this.isHtmlGraphic(s)),
            },
        ].filter((group) => group.scenes.length > 0);

        const findings: ReviewFinding[] = [];
        for (const group of groups) {
            const outcome = await this.reviewGroup(
                args,
                context,
                group.scenes,
                applicableChecks({ hasScan, isHtmlGraphic: group.isHtmlGraphic }),
            );
            if ('earlyResult' in outcome) {
                return outcome.earlyResult;
            }
            if (outcome.findings === undefined) {
                // One group's reviewer answered in prose instead of reporting. Block on the whole
                // review rather than merging a partial one: the re-review this forces will surface
                // the other group's findings again, and a partial pass is how a defect ships.
                return this.buildReviewResult(undefined, resolvedScenes, context.conversationId);
            }
            findings.push(...outcome.findings);
        }

        return this.buildReviewResult(findings, resolvedScenes, context.conversationId);
    }

    /** Whether this scene holds an HtmlAsset — a graphic the agent wrote, not a designer's template. */
    private isHtmlGraphic({ sceneIndex }: ResolvedScene): boolean {
        const scene = this.luminary.scenes[sceneIndex];
        return !!scene && scene.getAssetsByType(VideoData.HTML_ASSET_TYPE).length > 0;
    }

    /**
     * Renders one group of scenes and asks a reviewer about them against that group's checks.
     * Returns the findings, `undefined` when the reviewer never reported, or an `earlyResult` the
     * caller should return as-is (a user skip, or a scene that would not render).
     */
    private async reviewGroup(
        args: ReviewSceneImagesArgs,
        context: ToolExecutionContext,
        scenes: readonly ResolvedScene[],
        checks: readonly ReviewCheck[],
    ): Promise<{ earlyResult: EnhancedToolResult } | { findings: ReviewFinding[] | undefined }> {
        const messageContent: (TextContent | ImageContent)[] = [];
        for (const { sceneId, sceneIndex } of scenes) {
            // Re-checked each time round: rendering a scene is slow, so a skip clicked during a
            // multi-scene review should drop the rest rather than finish the batch and then ask
            // the vision model about a review the user has already waved off.
            if (isReviewSkipped(this.getState())) {
                return { earlyResult: { result: reviewSkippedResult(), isSuccess: true } };
            }
            const rendered = await this.renderScene(args, sceneId, sceneIndex);
            if ('error' in rendered) {
                return { earlyResult: { result: rendered.error, isSuccess: false } };
            }
            messageContent.push(...rendered.content);
        }

        messageContent.push({ type: 'text', text: buildReviewInstruction(checks) });

        const reportFindings = new ReportFindingsTool({
            luminary: this.luminary,
            dispatch: this.dispatch,
            getState: this.getState,
        });

        const conversation = this.createChildConversationForToolCall(context.toolCallId, {
            systemMessage: `You are reviewing frames rendered from a video scene.

You did not make this video and have no stake in it. Report what is actually in the frames. Your
judgement is the only check on work whose author cannot see it objectively, so a defect you soften
is a defect that ships.

When describing locations within an image, use terms like: top-left, top-center, top-right,
center-left, center, center-right, bottom-left, bottom-center, bottom-right.`,
            tools: [reportFindings],
            task: 'review_scene_images_tool',
            requiresVideoState: false,
        });

        conversation.addUserMessage(messageContent);

        try {
            const result = await conversation.executeWithLLM({
                parentActionGroup: context.parentActionGroup,
                stopTool: TOOL_NAMES.reportFindings,
            });
            if (!result.success) {
                // A skip cancels the vision call, which surfaces here as a failure.
                if (isReviewSkipped(this.getState())) {
                    return { earlyResult: { result: reviewSkippedResult(), isSuccess: true } };
                }
                return {
                    earlyResult: {
                        result: `Error during scene review: ${result.message || 'the reviewer did not return a result. Try again in a moment.'}`,
                        isSuccess: false,
                    },
                };
            }
        } catch (error) {
            if (isReviewSkipped(this.getState())) {
                return { earlyResult: { result: reviewSkippedResult(), isSuccess: true } };
            }
            return {
                earlyResult: {
                    result: `Error reviewing scenes: ${error instanceof Error ? error.message : 'Unknown error'}`,
                    isSuccess: false,
                },
            };
        }

        return { findings: reportFindings.captured };
    }

    /**
     * Applies the round cap per scene before formatting the gate. A scene released by the cap is
     * pulled out of the blocking findings and reported as accepted-with-residual instead, so a
     * defect the model can't fix stops blocking without silently vanishing from the result.
     */
    private buildReviewResult(
        captured: ReviewFinding[] | undefined,
        resolvedScenes: readonly ResolvedScene[],
        conversationId: string | undefined,
    ): EnhancedToolResult {
        if (captured === undefined) {
            return {
                result:
                    'The reviewer did not report a result. Nothing was checked — these scenes ' +
                    'still need review.',
                isSuccess: false,
            };
        }

        // Keyed on the scene's canonical id (never the raw, agent-echoed sceneId): the vision
        // model is free to answer with any spelling `resolveSceneById` accepts (a shorter or
        // longer prefix, the full uuid, different case), and a duplicate entry in scene_ids must
        // not be counted as two scenes. Dedupe the request the same way.
        const requestedCanonicalIds = new Set(
            resolvedScenes.map(({ sceneIndex }) => this.luminary.scenes[sceneIndex].id),
        );

        const findingsByCanonicalId = new Map<string, ReviewFinding[]>();
        // A finding whose scene_id can't be pinned to one of the requested scenes must never be
        // dropped — the whole reason this tool exists is that a defect silently disappearing is
        // worse than one that keeps blocking. It has no scene identity to key a per-scene round on,
        // so it is tracked per-conversation instead (below) and released by the same cap.
        const unattributedFindings: ReviewFinding[] = [];
        for (const finding of captured) {
            const resolved = resolveSceneById(this.luminary, this.getState(), finding.scene_id);
            const canonicalId = 'error' in resolved ? null : resolved.scene.id;
            if (canonicalId === null || !requestedCanonicalIds.has(canonicalId)) {
                unattributedFindings.push(finding);
                continue;
            }
            const existing = findingsByCanonicalId.get(canonicalId);
            if (existing) {
                existing.push(finding);
            } else {
                findingsByCanonicalId.set(canonicalId, [finding]);
            }
        }

        const conversationKey = conversationId ?? 'no-conversation';
        const blockingFindings: ReviewFinding[] = [];
        const blockingRepeated: boolean[] = [];
        const releasedNotes: string[] = [];

        // The round cap advances on the coarse identity (same defect, however it's worded this
        // time) so a reworded detail string can't reset an unfixable defect's count forever;
        // `repeated` (returned for the gate's "unchanged" wording) still needs the full,
        // byte-exact signature, since that message is a stronger claim than "still the same
        // defect" — it's "this specific wording came back, so the edit provably did nothing".
        const trackRound = (runKey: string, findings: readonly ReviewFinding[]) => {
            const signature = findingsSignature(findings);
            const repeated = lastSignatureByScene.get(runKey) === signature;
            lastSignatureByScene.set(runKey, signature);

            const identity = findingsIdentitySignature(findings);
            const sameDefect = lastIdentityByScene.get(runKey) === identity;
            lastIdentityByScene.set(runKey, identity);
            const rounds = sameDefect ? (blockingRoundsByScene.get(runKey) ?? 0) + 1 : 1;
            blockingRoundsByScene.set(runKey, rounds);

            return { rounds, repeated };
        };
        const clearRound = (runKey: string) => {
            // A fixed scene must not carry old rounds into a later regression.
            lastSignatureByScene.delete(runKey);
            lastIdentityByScene.delete(runKey);
            blockingRoundsByScene.delete(runKey);
        };

        if (unattributedFindings.length === 0) {
            clearRound(`${conversationKey}:__unattributed__`);
        } else {
            const runKey = `${conversationKey}:__unattributed__`;
            const { rounds, repeated } = trackRound(runKey, unattributedFindings);
            if (rounds > MAX_REVIEW_ROUNDS) {
                const list = unattributedFindings
                    .map(
                        (finding) =>
                            `- [${finding.check}] scene ${finding.scene_id}: ${finding.detail}`,
                    )
                    .join('\n');
                releasedNotes.push(
                    `Accepted with the defect(s) noted after ${MAX_REVIEW_ROUNDS} fix rounds — ` +
                        `reported against a scene id that did not resolve to one of the reviewed ` +
                        `scenes, so it stayed unattributed:\n${list}`,
                );
            } else {
                blockingFindings.push(...unattributedFindings);
                blockingRepeated.push(repeated);
            }
        }

        for (const canonicalId of requestedCanonicalIds) {
            const runKey = `${conversationKey}:${canonicalId}`;
            const sceneFindings = findingsByCanonicalId.get(canonicalId);
            if (!sceneFindings || sceneFindings.length === 0) {
                clearRound(runKey);
                continue;
            }

            const { rounds, repeated } = trackRound(runKey, sceneFindings);

            if (rounds > MAX_REVIEW_ROUNDS) {
                const list = sceneFindings
                    .map(
                        (finding) =>
                            `- [${finding.check}] scene ${finding.scene_id}: ${finding.detail}`,
                    )
                    .join('\n');
                releasedNotes.push(
                    `Scene ${sceneFindings[0].scene_id}: accepted with the defect(s) noted after ` +
                        `${MAX_REVIEW_ROUNDS} fix rounds:\n${list}`,
                );
            } else {
                blockingFindings.push(...sceneFindings);
                blockingRepeated.push(repeated);
            }
        }

        if (blockingFindings.length === 0) {
            if (releasedNotes.length === 0) {
                return {
                    result: `Reviewed ${requestedCanonicalIds.size} scene(s): no defects found.`,
                    isSuccess: true,
                };
            }
            return { result: releasedNotes.join('\n'), isSuccess: true };
        }

        // Only "unchanged" when every blocking scene's set is unchanged — a scene with a fresh
        // finding this round means the batch as a whole did move.
        const overallRepeated = blockingRepeated.length > 0 && blockingRepeated.every(Boolean);
        const gate = formatReviewGate(blockingFindings, { repeated: overallRepeated });
        return {
            result: releasedNotes.length > 0 ? `${gate}\n\n${releasedNotes.join('\n')}` : gate,
            isSuccess: false,
        };
    }

    private async renderScene(
        args: ReviewSceneImagesArgs,
        sceneId: string,
        sceneIndex: number,
    ): Promise<{ content: (TextContent | ImageContent)[] } | { error: string }> {
        if (sceneIndex < 0 || sceneIndex >= this.luminary.scenes.length) {
            return {
                error: `Error: scene index ${sceneIndex} is out of bounds. Video has ${this.luminary.scenes.length} scenes.`,
            };
        }
        const scene = this.luminary.scenes[sceneIndex];
        if (!scene) {
            return { error: `Error: No scene found at index ${sceneIndex}.` };
        }

        const progressPoints =
            args.seconds !== undefined
                ? [Math.min(1, (args.seconds * 1000) / scene.duration)]
                : CAPTURE_MODE_PROGRESS.timeline_scan;
        const frames = await captureSceneFrames(
            this.luminary,
            scene,
            progressPoints,
            SCREENSHOT_MAX_EDGE.high,
        );
        if (!frames.length) {
            return {
                error: `Error: could not render scene ${sceneDisplayId(this.luminary, scene.id)}.`,
            };
        }

        const spoken = (getSceneTranscriptTokens(this.getState(), scene) ?? []).filter(
            (token) => !token.isPause,
        );
        const timed = spoken.length ? timeNarrationWords(scene, spoken) : null;

        const content: (TextContent | ImageContent)[] = [
            {
                type: 'text',
                text:
                    frames.length === 1
                        ? `Scene ${sceneId} — one frame at ${frames[0].label} of the scene's timeline:`
                        : `Scene ${sceneId} — ${frames.length} frames across the scene's timeline:`,
            },
        ];
        // Narration present but its timings can't be mapped onto the scene: attach it once as
        // plain text rather than fabricating a per-frame percentage.
        if (spoken.length && !timed) {
            content.push({
                type: 'text',
                text: formatNarration(spoken.map((token) => token.word).join(' ')),
            });
        }
        frames.forEach((frame, index) => {
            content.push({
                type: 'text',
                text: frameLabel(
                    frame.label,
                    (progressPoints[index] * scene.duration) / 1000,
                    timed,
                ),
            });
            content.push({ type: 'image_url', image_url: { url: frame.dataUrl } });
        });
        return { content };
    }
}

/**
 * The label for a captured frame, folding in the narration spoken at that point of the scene when
 * the scene's word timings are available: "at 4.0s (saying: grew **forty** percent):". Falls back
 * to the bare "at N.Ns:" label when there's no mappable narration. Both are measured from the
 * scene's start, matching the unit the word timings are in.
 */
function frameLabel(label: string, seconds: number, timed: TimedWord[] | null): string {
    if (!timed) {
        return `at ${label}:`;
    }
    const window = narrationWindowAt(timed, seconds);
    if (!window) {
        return `at ${label}:`;
    }
    const verb = window.activeIndex >= 0 ? 'saying' : 'around';
    return `at ${label} (${verb}: ${formatNarrationWindow(window)}):`;
}
